if (!customElements.get('manual-store-switcher')) {
  customElements.define('manual-store-switcher', class extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.button = this.querySelector('button');
      this.panel = this.querySelector('.disclosure__list-wrapper');
      this.link = this.querySelector('a');
      this.setOpen(false);

      this.button.addEventListener('click', () => this.setOpen(this.panel.hidden), options);
      this.link.addEventListener('click', () => this.setOpen(false), options);
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setOpen(false);
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.setOpen(true);
          this.link.focus();
        }
      }, options);
      // The drawer also handles Escape on keyup; close only this dropdown first.
      this.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape' || this.panel.hidden) return;
        event.stopPropagation();
        this.setOpen(false);
        this.button.focus();
      }, options);
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.setOpen(false);
      }, options);
      window.addEventListener('resize', () => this.setOpen(false), options);

      if (this.classList.contains('manual-store-switcher--floating')) {
        this.watchFloatingSpace(options);
      }
    }

    disconnectedCallback() {
      this.listeners.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.spaceFrame);
    }

    setOpen(open) {
      this.panel.hidden = !open;
      this.button.setAttribute('aria-expanded', String(open));
      if (open && this.classList.contains('manual-store-switcher--floating')) {
        cancelAnimationFrame(this.spaceFrame);
        this.spaceFrame = requestAnimationFrame(() => this.updateFloatingSpace());
      }
    }

    watchFloatingSpace(options) {
      this.observer?.disconnect();
      const schedule = () => {
        cancelAnimationFrame(this.spaceFrame);
        this.spaceFrame = requestAnimationFrame(() => this.updateFloatingSpace());
      };
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) schedule();
      });
      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden'],
      });
      window.addEventListener('scroll', schedule, { ...options, passive: true });
      window.addEventListener('resize', schedule, options);
      document.addEventListener('load', schedule, { ...options, capture: true });
      document.addEventListener('transitionend', schedule, options);
      document.addEventListener('animationend', schedule, options);
      schedule();
    }

    updateFloatingSpace() {
      const footer = document.querySelector('footer.footer')?.getBoundingClientRect();
      const mobileMenuOpen =
        window.matchMedia('(max-width: 989px)').matches && document.querySelector('header-drawer > details[open]');
      // Yield to the footer's own switcher and to the original app selector if re-enabled.
      let obscured = Boolean(
        mobileMenuOpen ||
        (footer && footer.top < window.innerHeight && footer.bottom > 0) ||
        document.querySelector('.blz_localization_overlay_selector')
      );

      // Hit-test both behind and above our control, including iframe-based widgets.
      // Do not alter the cookie notice, chat widget, drawer or popup itself.
      const areas = [this.button.getBoundingClientRect()];
      if (!this.panel.hidden) areas.push(this.panel.getBoundingClientRect());
      for (const area of areas) {
        if (obscured) break;
        for (const x of [area.left + 1, (area.left + area.right) / 2, area.right - 1]) {
          for (const y of [area.top + 1, (area.top + area.bottom) / 2, area.bottom - 1]) {
            for (const hit of document.elementsFromPoint(x, y)) {
              if (this.contains(hit)) continue;
              let element = hit;
              while (element && element !== document.body) {
                if (getComputedStyle(element).position === 'fixed') {
                  obscured = true;
                  break;
                }
                element = element.parentElement;
              }
            }
          }
        }
      }

      if (obscured) {
        const hadFocus = this.contains(document.activeElement);
        this.setOpen(false);
        if (hadFocus) {
          const footerButton = document.querySelector('.manual-store-switcher--footer button');
          if (footer && footer.top < window.innerHeight && footerButton) {
            footerButton.focus({ preventScroll: true });
          } else {
            document.activeElement.blur();
          }
        }
      }
      this.toggleAttribute('data-floating-obscured', obscured);
    }
  });
}
